import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Exports a React component/DOM element to PDF with proper page breaks
 * @param {HTMLElement} element - The DOM element to convert
 * @param {string} filename - The output PDF filename
 * @param {Object} options - Configuration options
 * @param {boolean} options.returnBlob - If true, returns blob instead of downloading
 * @returns {Promise<{success: boolean, pages: number, blob?: Blob}>}
 */
export const exportToPDF = async (element, filename, options = {}) => {
  const {
    scale = 2,
    useCORS = true,
    pageFormat = 'a4',
    orientation = 'portrait',
    margins = { top: 20, bottom: 20, left: 20, right: 20 },
    headerInfo = null,
    returnBlob = false
  } = options;

  try {
    console.log(`📄 Starting PDF export: ${filename}`);

    // Create PDF document
    const pdf = new jsPDF(orientation, 'mm', pageFormat);
    
    // Calculate page dimensions
    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const contentWidth = pageWidth - margins.left - margins.right;
    const contentHeight = pageHeight - margins.top - margins.bottom;

    // Find the entire report content
    const reportContent = element.querySelector('.pdf-optimized') || element;
    
    // Get the header section separately
    const headerSection = reportContent.querySelector('[data-pdf-section="header"]');
    
    // Capture header section if it exists
    if (headerSection) {
      console.log(`📸 Capturing header section...`);
      const canvas = await html2canvas(headerSection, {
        scale,
        useCORS,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: headerSection.scrollWidth,
        windowHeight: headerSection.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      
      // Add header to first page
      pdf.addImage(imgData, 'JPEG', margins.left, margins.top, contentWidth, imgHeight);
    }

    // Get all sections except header
    const sections = Array.from(reportContent.querySelectorAll('[data-pdf-section]'))
      .filter(section => section.getAttribute('data-pdf-section') !== 'header');
    
    console.log(`📊 Found ${sections.length} sections (excluding header)`);
    
    let currentY = margins.top + (headerSection ? 
      (headerSection.scrollHeight * contentWidth / headerSection.scrollWidth) + 20 : 
      margins.top);
    
    // Process each section sequentially
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const sectionType = section.getAttribute('data-pdf-section');
      
      // Skip page-break sections
      if (sectionType === 'page-break') {
        pdf.addPage();
        currentY = margins.top; // Reset Y position, NO header on new pages
        continue;
      }
      
      console.log(`📸 Capturing section: ${sectionType}`);
      
      // Check if we need a new page for this section
      if (currentY > (contentHeight * 0.8) && i > 0) {
        pdf.addPage();
        currentY = margins.top; // NO header on new pages
      }
      
      // Capture this section
      const canvas = await html2canvas(section, {
        scale,
        useCORS,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: section.scrollWidth,
        windowHeight: section.scrollHeight,
        onclone: (clonedDoc) => {
          const clonedSection = clonedDoc.querySelector(`[data-pdf-section="${sectionType}"]`);
          if (clonedSection) {
            clonedSection.style.width = contentWidth + 'mm';
            clonedSection.style.maxWidth = contentWidth + 'mm';
            clonedSection.style.display = 'block';
          }
        }
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      
      // Check if it fits on current page
      if (currentY + imgHeight > (contentHeight + margins.top)) {
        pdf.addPage();
        currentY = margins.top; // NO header on new pages
      }
      
      pdf.addImage(imgData, 'JPEG', margins.left, currentY, contentWidth, imgHeight);
      currentY += imgHeight + 10;
    }
    
    // Now handle map containers separately
    const mapContainers = reportContent.querySelectorAll('.pdf-map-container, [data-pdf-map]');
    console.log(`📊 Found ${mapContainers.length} map containers`);
    
    for (let i = 0; i < mapContainers.length; i++) {
      const mapContainer = mapContainers[i];
      console.log(`📸 Capturing map ${i + 1}/${mapContainers.length}...`);
      
      // Force new page for each map (NO header)
      pdf.addPage();
      currentY = margins.top;
      
      // Temporarily adjust map container for capture
      const originalStyles = {
        display: mapContainer.style.display,
        height: mapContainer.style.height,
        width: mapContainer.style.width
      };
      
      mapContainer.style.display = 'block';
      mapContainer.style.width = '100%';
      mapContainer.style.height = 'auto';
      
      // ADDED: Wait for maps to load before capturing
      console.log('⏳ Waiting for maps to render...');
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay
      
      const canvas = await html2canvas(mapContainer, {
        scale,
        useCORS,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: contentWidth * 3.78, // Convert mm to pixels (96 DPI)
        windowHeight: contentHeight * 3.78,
        onclone: (clonedDoc) => {
          const clonedMap = clonedDoc.querySelector(`[data-pdf-map-index="${i}"]`) || 
                           clonedDoc.querySelector(`[data-pdf-map="${i}"]`);
          if (clonedMap) {
            clonedMap.style.width = '100%';
            clonedMap.style.height = 'auto';
            clonedMap.style.display = 'block';
          }
        }
      });
      
      // Restore original styles
      Object.keys(originalStyles).forEach(key => {
        mapContainer.style[key] = originalStyles[key];
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      
      // Center vertically on page
      const yPosition = margins.top + Math.max(0, (contentHeight - imgHeight) / 2);
      
      pdf.addImage(imgData, 'JPEG', margins.left, yPosition, contentWidth, imgHeight);
    }

    // Get total pages
    const totalPages = pdf.internal.getNumberOfPages();
    console.log(`📄 PDF created with ${totalPages} page(s)`);

    // Save or return the PDF
    if (returnBlob) {
      const blob = pdf.output('blob');
      console.log(`✅ PDF blob created: ${(blob.size / (1024 * 1024)).toFixed(2)} MB`);
      return { success: true, pages: totalPages, blob };
    } else {
      pdf.save(filename);
      console.log(`✅ PDF downloaded successfully: ${filename}`);
      return { success: true, pages: totalPages };
    }
  } catch (error) {
    console.error('❌ Error generating PDF:', error);
    throw new Error('Failed to generate PDF: ' + error.message);
  }
};

/**
 * Simplified filename generator
 */
export const generateReportFilename = (businessName, startDate, endDate) => {
  const sanitizedName = businessName
    .replace(/[^a-z0-9\s]/gi, '')
    .trim()
    .replace(/\s+/g, '_')
    .toLowerCase();

  const formattedStart = formatDate(startDate);
  const formattedEnd = formatDate(endDate);

  return `Monatlicher_Leistungsbericht_${sanitizedName}_${formattedStart}_bis_${formattedEnd}.pdf`;
};

/**
 * Format date to YYYY-MM-DD format
 */
const formatDate = (date) => {
  if (typeof date === 'string') {
    return date.split('T')[0];
  }

  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();

  return `${year}-${month}-${day}`;
};

/**
 * Prepare element for PDF capture
 */
export const prepareElementForPDF = (element) => {
  if (!element) return;
  
  element.classList.add('pdf-export-mode');
  
  const mapContainers = element.querySelectorAll('[data-pdf-map]');
  mapContainers.forEach((container, index) => {
    container.setAttribute('data-pdf-map-index', index);
  });
  
  console.log(`🔧 Prepared ${mapContainers.length} map containers for PDF export`);
};

/**
 * Clean up after PDF export
 */
export const cleanupAfterPDF = (element) => {
  if (!element) return;
  
  element.classList.remove('pdf-export-mode');
  
  const mapContainers = element.querySelectorAll('[data-pdf-map-index]');
  mapContainers.forEach(container => {
    container.removeAttribute('data-pdf-map-index');
  });
};

/**
 * Main export function for reports
 */
export const exportReportToPDF = async (element, filename, options = {}) => {
  const defaultOptions = {
    scale: 2,
    useCORS: true,
    pageFormat: 'a4',
    orientation: 'portrait',
    margins: { top: 20, bottom: 20, left: 20, right: 20 },
    ...options
  };

  return await exportToPDF(element, filename, defaultOptions);
};